import { motion } from "framer-motion";
import { Award } from "lucide-react";
import Autoplay from "embla-carousel-autoplay";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import { useLanguage } from "@/i18n/LanguageContext";

import googleCloudAward from "@/assets/awards/google-cloud-award.png";
import lifeTriplAir from "@/assets/awards/life-tripl-air.png";
import techcrunchDisrupt from "@/assets/awards/techcrunch-disrupt.webp";
import luissLogo from "@/assets/awards/luiss-logo.png";

export const AwardsSection = () => {
  const { t } = useLanguage();

  const awards = [
    {
      title: t.awards.items.lifeTriplAir.title,
      subtitle: t.awards.items.lifeTriplAir.subtitle,
      image: lifeTriplAir,
      imageAlt: "Life Tripl-Air EU sustainability project for AI waste management",
    },
    {
      title: t.awards.items.googleAI.title,
      subtitle: t.awards.items.googleAI.subtitle,
      image: googleCloudAward,
      imageAlt: "Google Cloud AI Groundbreaker Award for smart waste management innovation",
    },
    {
      title: t.awards.items.luiss.title,
      subtitle: t.awards.items.luiss.subtitle,
      image: luissLogo,
      imageAlt: "LUISS University Non Sprecare Award for circular economy innovation",
    },
    {
      title: t.awards.items.techcrunch.title,
      subtitle: t.awards.items.techcrunch.subtitle,
      image: techcrunchDisrupt,
      imageAlt: "TechCrunch Disrupt 2025 - AI sorting solutions startup selection",
    },
  ];

  return (
    <section className="py-24 lg:py-32 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
            <Award className="w-4 h-4" />
            <span className="text-sm font-medium">{t.awards.badge}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-4">
            {t.awards.title}
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto font-light">
            {t.awards.subtitle}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Carousel
            opts={{ align: "start", loop: true }}
            plugins={[
              Autoplay({
                delay: 4000,
                stopOnInteraction: true,
                stopOnMouseEnter: true,
              }),
            ]}
            className="w-full"
          >
            <CarouselContent className="-ml-4 lg:-ml-6">
              {awards.map((award, index) => (
                <CarouselItem key={index} className="pl-4 lg:pl-6 basis-full sm:basis-1/2 lg:basis-1/4">
                  <div className="group bg-card border border-border rounded-2xl p-8 h-full hover:shadow-lg transition-all duration-500 hover:-translate-y-1 hover:border-primary/30">
                    <div className="flex flex-col items-center text-center">
                      <div className="h-16 flex items-center justify-center mb-6 overflow-hidden">
                        <img
                          src={award.image}
                          alt={award.imageAlt}
                          className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <h3 className="font-semibold text-foreground mb-2">
                        {award.title}
                      </h3>
                      <p className="text-sm text-muted-foreground font-light">
                        {award.subtitle}
                      </p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-0 -translate-x-1/2 bg-background border-border hover:bg-muted" />
            <CarouselNext className="right-0 translate-x-1/2 bg-background border-border hover:bg-muted" />
          </Carousel>
        </motion.div>
      </div>
    </section>
  );
};
